/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Feedback (strategy 9.5, mockup admin-9, "Build as drawn"): the inbox of what people said right after Gyro did
 * something, from GET /v1/admin/pulse/feedback, one row per entry; filters for rating, undo, comment, reviewed, domain,
 * tool and date; every column sortable; a row's drawer with the full entry, the comment, the scrubbed ask, Mark reviewed,
 * Add to problem… and Open the record. Nothing here names a person; the drawer says so in its footer. `?key=` opens an
 * entry; `?reviewed=0` lands on the not-yet-reviewed ones (the Overview's link).
 */
import { useState } from 'react';
import { Search, ThumbsUp } from 'lucide-react';
import { api } from '../../lib/api';
import {
  OPEN_PROBLEM_STATUSES, domainLabel, feedbackChip, healthChip, outcomeChip, pulseExportHref, pulseHref, sortParam, surfaceLabel, toggleSort, whenText,
  type PulseFeedbackRow, type PulseProblem, type PulseRecord, type SortState,
} from '../../lib/pulse';
import { navigate } from '../../shell/router';
import { SaveNote } from '../../components/SaveNote';
import { Button, Checkbox, Dialog, Drawer, EmptyState, Field, Icon, Input, PageHeader, Panel, Select, Skeleton, Stack, StatusChip, Table, type Column } from '../../ui';
import { count } from '../../ui/format';
import { AnonymousFoot, Chip, DEFAULT_FILTERS, DomainSelect, ExportButton, Facts, PulseError, RangeSelect, filterParams, useLoad, usePagedList, useQueryParam, type PulseFilters } from './common';

const RATING_FILTERS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Rating: All' }, { value: 'worked', label: 'Worked' }, { value: 'partly', label: 'Partly' }, { value: 'failed', label: "Didn't work" },
  { value: 'thumb-up', label: 'Thumbs up' }, { value: 'thumb-down', label: 'Thumbs down' },
];
const TEXT_COLUMNS = new Set(['domain', 'intent', 'tool', 'rating', 'outcome']);

export function PulseFeedback({ canEdit }: { canEdit: boolean }) {
  const [reviewedParam] = useQueryParam('reviewed');
  const [filters, setFilters] = useState<PulseFilters>(DEFAULT_FILTERS);
  const [q, setQ] = useState('');
  const [rating, setRating] = useState('');
  const [tool, setTool] = useState('');
  const [undoOnly, setUndoOnly] = useState(false);
  const [commentOnly, setCommentOnly] = useState(false);
  const [unreviewedOnly, setUnreviewedOnly] = useState(reviewedParam === '0');
  const [sort, setSort] = useState<SortState>({ key: 'atMs', direction: 'desc' });
  const [selectedKey, setSelectedKey] = useQueryParam('key');
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const params = {
    ...filterParams(filters), q: q.trim() || undefined, tool: tool.trim() || undefined,
    rating: rating && !rating.startsWith('thumb-') ? rating : undefined, thumb: rating.startsWith('thumb-') ? rating.slice('thumb-'.length) : undefined,
    undo: undoOnly, comment: commentOnly, reviewed: unreviewedOnly ? 0 : undefined, sort: sortParam(sort),
  };
  const list = usePagedList((cursor) => api.pulseFeedback({ ...params, cursor, limit: 50 }), JSON.stringify(params));
  const selected = list.items.find((row) => row.key === selectedKey) ?? null;

  const columns: Array<Column<PulseFeedbackRow>> = [
    { key: 'atMs', header: 'When', sortable: true, cell: (row) => whenText(row.atMs) },
    { key: 'domain', header: 'Domain', sortable: true, hideOnPhone: true, cell: (row) => domainLabel(row.domain) },
    { key: 'intent', header: 'Intent', sortable: true, cell: (row) => row.intent },
    { key: 'tool', header: 'Action', sortable: true, mono: true, hideOnPhone: true, cell: (row) => row.tool ?? '—' },
    { key: 'rating', header: 'Rating', sortable: true, cell: (row) => <Chip spec={feedbackChip(row)} /> },
    { key: 'undo', header: 'Undo', sortable: true, hideOnPhone: true, cell: (row) => (row.undo ? <span className="ui-text-bad">had to undo it</span> : <span className="ui-quiet">—</span>) },
    { key: 'tags', header: 'Tags', hideOnPhone: true, cell: (row) => (row.tags.length ? <span className="ui-chips">{row.tags.map((tag) => <StatusChip key={tag} tone="neutral" plain>{tag}</StatusChip>)}</span> : <span className="ui-quiet">—</span>) },
    { key: 'comment', header: 'Comment', hideOnPhone: true, className: 'ui-clip', cell: (row) => (row.comment ? <span className="ui-clip" title={row.comment}>{row.comment}</span> : <span className="ui-quiet">—</span>) },
    { key: 'outcome', header: 'Engine said', sortable: true, hideOnPhone: true, cell: (row) => <Chip spec={outcomeChip(row.outcome)} /> },
    { key: 'reviewedAt', header: 'Reviewed', sortable: true, cell: (row) => (row.reviewedAt ? <span className="ui-text-ok">{whenText(row.reviewedAt)}</span> : <span className="ui-quiet">not yet</span>) },
  ];

  const unreviewed = list.items.filter((row) => !row.reviewedAt).length;

  return (
    <>
      <PageHeader
        meta={<span>What people said right after Gyro did something.{list.load.state === 'ready' ? ` ${count(list.total)} entries${unreviewedOnly ? '' : `, ${count(unreviewed)} not yet reviewed on this page`}.` : ''} Anonymous: no names, no devices.</span>}
        actions={(
          <>
            <RangeSelect value={filters.days} onChange={(days) => setFilters({ ...filters, days })} />
            <ExportButton href={pulseExportHref('feedback', params)} />
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          <Panel flush title="Entries" description="One row per card or thumb; click a row for the whole entry">
            <div className="ui-panel-pad ui-panel-pad--divided">
              <div className="ui-form">
                <div className="pulse-filters">
                  <Input className="ui-grow" prefix={<Icon icon={Search} size="sm" />} aria-label="Search comments" placeholder="Search comments…" value={q} onChange={(event) => setQ(event.target.value)} />
                  <Select size="sm" className="ui-w-auto" aria-label="Rating" value={rating} onChange={(event) => setRating(event.target.value)}>
                    {RATING_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </Select>
                  <DomainSelect value={filters.domain} onChange={(domain) => setFilters({ ...filters, domain })} />
                  <Input size="sm" className="ui-w-auto" aria-label="Tool" placeholder="Tool: All" value={tool} onChange={(event) => setTool(event.target.value)} />
                </div>
                <div className="ui-actions">
                  <Checkbox label="Had to undo it only" checked={undoOnly} onChange={(event) => setUndoOnly(event.target.checked)} />
                  <Checkbox label="With a comment only" checked={commentOnly} onChange={(event) => setCommentOnly(event.target.checked)} />
                  <Checkbox label="Not yet reviewed only" checked={unreviewedOnly} onChange={(event) => setUnreviewedOnly(event.target.checked)} />
                </div>
              </div>
            </div>
            {list.load.state === 'failed' ? <div className="ui-panel-pad"><PulseError title="The feedback could not be loaded" error={list.load.error} onRetry={list.reload} retrying={list.reloading} /></div> : (
              <Table
                label="Feedback entries"
                columns={columns}
                rows={list.items}
                rowKey={(row) => row.key}
                loading={list.load.state === 'loading'}
                skeletonRows={7}
                selectedKey={selectedKey}
                onRowClick={(row) => setSelectedKey(row.key, { replace: false })}
                sort={{ key: sort.key, direction: sort.direction, onSort: (key) => setSort(toggleSort(sort, key, !TEXT_COLUMNS.has(key))) }}
                more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
                empty={<EmptyState compact icon={ThumbsUp} title="No feedback in this range">Cards and thumbs appear here as people answer them, with the pulse-cards flag on.</EmptyState>}
              />
            )}
            <div className="ui-panel-pad ui-panel-pad--top ui-panel-pad--note"><SaveNote note={note} /></div>
          </Panel>
        </Stack>
      </div>
      <FeedbackDrawer
        entry={selected}
        pendingKey={selectedKey && !selected ? selectedKey : null}
        canEdit={canEdit}
        onClose={() => setSelectedKey(null)}
        onChanged={(next) => { list.setItems((items) => items.map((row) => (row.key === next.key ? { ...row, ...next } : row))); }}
        onNote={setNote}
      />
    </>
  );
}

interface DrawerProps {
  entry: PulseFeedbackRow | null;
  /** A `?key=` that is not on the loaded page: the drawer says so instead of showing nothing. */
  pendingKey: string | null;
  canEdit: boolean;
  onClose: () => void;
  onChanged: (entry: PulseFeedbackRow) => void;
  onNote: (note: { ok: boolean; text: string }) => void;
}

/** One entry: the facts, the comment in full, the scrubbed ask from the record, and the admin's three actions. */
function FeedbackDrawer({ entry, pendingKey, canEdit, onClose, onChanged, onNote }: DrawerProps) {
  const [busy, setBusy] = useState<'reviewed' | 'problem' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const record = useLoad<PulseRecord | null>(() => (entry ? api.pulseRecord(entry.askId) : Promise.resolve(null)), entry?.askId ?? '');
  const open = entry !== null || pendingKey !== null;
  const spec = entry ? feedbackChip(entry) : null;

  const markReviewed = async () => {
    if (!entry) return;
    setBusy('reviewed');
    setError(null);
    try {
      const result = await api.pulseFeedbackReviewed(entry.key, !entry.reviewedAt);
      onChanged(result.entry ?? { ...entry, reviewedAt: entry.reviewedAt ? null : Date.now() });
      onNote({ ok: true, text: entry.reviewedAt ? 'Marked as not reviewed.' : 'Marked reviewed. The change is in the audit trail.' });
    } catch (failure) {
      setError(failure instanceof Error && failure.message ? failure.message : 'The service refused the change.');
    } finally {
      setBusy(null);
    }
  };

  const addToProblem = async (target: { problemId: string } | { new: { title: string } }) => {
    if (!entry) return;
    setBusy('problem');
    setError(null);
    try {
      const result = await api.pulseFeedbackToProblem(entry.key, target);
      onChanged(result.entry ?? { ...entry, problemId: result.problem.problemId });
      onNote({ ok: true, text: `Added as evidence to ${result.problem.problemId} · ${result.problem.title}.` });
      setAddOpen(false);
    } catch (failure) {
      setError(failure instanceof Error && failure.message ? failure.message : 'The service refused the change.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={entry && spec ? `Feedback · ${spec.label}${entry.undo ? ' · Had to undo it' : ''}` : 'Feedback'}
        description={entry ? `${whenText(entry.atMs)} · ${surfaceLabel(entry.surface)}` : undefined}
        className="ui-drawer--wide"
        footer={<AnonymousFoot />}
      >
        {!entry ? <EmptyState compact icon={ThumbsUp} title="This entry is not on the page">Widen the date range or clear the filters, then open it again.</EmptyState> : (
          <div className="ui-form">
            <Facts rows={[
              ['When', `${whenText(entry.atMs)} · ${surfaceLabel(entry.surface)}`],
              ['Domain / intent', `${domainLabel(entry.domain)} · ${entry.intent}`],
              ['Action', entry.tool ? <span className="ui-cell-stack"><span><code>{entry.tool}</code>{entry.action ? ` · ${entry.action}` : ''}</span><span><Chip spec={outcomeChip(entry.outcome)} /></span></span> : <span>a plain answer · <Chip spec={outcomeChip(entry.outcome)} /></span>],
              ['Engine said', `${outcomeChip(entry.outcome).label}${entry.outcomeBy ? ` (${entry.outcomeBy})` : ''}${entry.healthAtTime ? ` · health at the time: ${healthChip(entry.healthAtTime).label}` : ''}`],
              ['Tags', entry.tags.length ? <span className="ui-chips">{entry.tags.map((tag) => <StatusChip key={tag} tone="neutral" plain>{tag}</StatusChip>)}</span> : 'none'],
              ['Episode', entry.episode ?? 'nothing further in the conversation'],
              ['Problem', entry.problemId ? <a href={pulseHref.problem(entry.problemId)} onClick={(event) => { event.preventDefault(); navigate(pulseHref.problem(entry.problemId!)); }}>{entry.problemId}</a> : 'not yet evidence for a problem'],
            ]} />
            {entry.comment ? <blockquote className="pulse-quote">{`“${entry.comment}”`}</blockquote> : <p className="ui-para ui-quiet">No comment was written.</p>}
            {record.load.state === 'loading' ? <Skeleton size="lg" /> : null}
            {record.load.state === 'ready' && record.load.data ? (
              <p className="pulse-text"><strong>Ask (scrubbed):</strong> {record.load.data.questionText ?? (record.load.data.textErasedAt ? 'erased' : 'not kept')}</p>
            ) : null}
            {record.load.state === 'failed' ? <p className="ui-para ui-quiet">The record behind this entry could not be read just now.</p> : null}
            {error ? <SaveNote note={{ ok: false, text: error }} /> : null}
            <div className="pulse-actions">
              {canEdit ? <Button variant="primary" busy={busy === 'reviewed'} onClick={() => void markReviewed()}>{entry.reviewedAt ? 'Mark not reviewed' : 'Mark reviewed'}</Button> : null}
              {canEdit ? <Button onClick={() => setAddOpen(true)} disabled={busy !== null}>Add to problem…</Button> : null}
              <Button onClick={() => navigate(pulseHref.record(entry.askId))}>Open the record</Button>
            </div>
          </div>
        )}
      </Drawer>
      {entry ? <AddToProblemDialog open={addOpen} entry={entry} busy={busy === 'problem'} error={error} onCancel={() => { setAddOpen(false); setError(null); }} onAdd={(target) => void addToProblem(target)} /> : null}
    </>
  );
}

/** Add to problem…: an open problem from the list, or a new one with a title. */
function AddToProblemDialog({ open, entry, busy, error, onCancel, onAdd }: { open: boolean; entry: PulseFeedbackRow; busy: boolean; error: string | null; onCancel: () => void; onAdd: (target: { problemId: string } | { new: { title: string } }) => void }) {
  const [choice, setChoice] = useState<string>('new');
  const [title, setTitle] = useState('');
  const problems = useLoad<PulseProblem[]>(() => (open ? api.pulseProblems({ sort: '-impact', limit: 100 }).then((page) => page.items.filter((problem) => OPEN_PROBLEM_STATUSES.includes(problem.status))) : Promise.resolve([])), open ? 'open' : 'closed');
  const ready = choice === 'new' ? title.trim().length > 0 : choice !== '';
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title="Add to problem"
      description={`This entry becomes evidence for the problem. ${domainLabel(entry.domain)} · ${entry.intent}`}
      footer={(
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={!ready} onClick={() => onAdd(choice === 'new' ? { new: { title: title.trim() } } : { problemId: choice })}>Add as evidence</Button>
        </>
      )}
    >
      <div className="ui-form">
        <Field label="Problem" help={problems.load.state === 'failed' ? 'The open problems could not be read; a new problem can still be opened.' : undefined}>
          <Select value={choice} onChange={(event) => setChoice(event.target.value)}>
            <option value="new">A new problem…</option>
            {(problems.load.state === 'ready' ? problems.load.data : []).map((problem) => <option key={problem.problemId} value={problem.problemId}>{problem.problemId} · {problem.title}</option>)}
          </Select>
        </Field>
        {choice === 'new' ? (
          <Field label="Title" help="What is going wrong, in one line; the domain is the entry's.">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} placeholder={`${domainLabel(entry.domain)}: ${entry.intent}…`} />
          </Field>
        ) : null}
        {error ? <SaveNote note={{ ok: false, text: error }} /> : null}
      </div>
    </Dialog>
  );
}
